/**
 * Ajusta a cor do tema para texto legível sobre fundo branco.
 *
 * A --primary muda por sistema (2Safe, Feev, Acordo Fácil) e por credor white label, e a
 * corPrimaria do credor pode ser clara demais para título ou para texto branco no botão. Aqui a
 * cor é escurecida só o necessário para chegar a 4.5:1, sem trocar o tom da marca.
 */

type Rgb = [number, number, number];

const BRANCO: Rgb = [255, 255, 255];

/** Aceita o que getComputedStyle devolve: "#00826a", "#08a", "rgb(96, 36, 152)". */
export function lerCor(valor: string): Rgb | null {
  const cor = (valor || '').trim();

  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})([0-9a-f]{2})?$/i.exec(cor);
  if (hex) {
    const digitos = hex[1].length === 3 ? hex[1].split('').map(d => d + d).join('') : hex[1];
    return [0, 2, 4].map(i => parseInt(digitos.substring(i, i + 2), 16)) as Rgb;
  }

  const rgb = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i.exec(cor);
  if (rgb)
    return [+rgb[1], +rgb[2], +rgb[3]];

  return null;
}

function luminancia([r, g, b]: Rgb): number {
  const canal = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b);
}

export function contraste(a: Rgb, b: Rgb): number {
  const [maior, menor] = [luminancia(a), luminancia(b)].sort((x, y) => y - x);
  return (maior + 0.05) / (menor + 0.05);
}

function escurecer([r, g, b]: Rgb, fracao: number): Rgb {
  return [r, g, b].map(c => Math.round(c * (1 - fracao))) as Rgb;
}

export function paraCss([r, g, b]: Rgb): string {
  return `rgb(${r}, ${g}, ${b})`;
}

/** A própria cor, ou ela escurecida em passos de 5% até ler sobre branco. */
export function corLegivelSobreBranco(cor: Rgb, minimo = 4.5): Rgb {
  for (let fracao = 0; fracao <= 0.9; fracao += 0.05) {
    const candidata = escurecer(cor, fracao);
    if (contraste(candidata, BRANCO) >= minimo)
      return candidata;
  }
  return [33, 37, 41];
}

/** Texto branco quando lê sobre a cor; senão, quase preto. */
export function textoSobre(cor: Rgb, minimo = 4.5): string {
  return contraste(cor, BRANCO) >= minimo ? '#fff' : '#212529';
}
